import { useEffect, useCallback, useRef } from "react";
import { presenceApi } from "../services/api/presenceApi";
import { API_BASE_URL } from "../config/apiConfig";

/**
 * PRESENCE TRACKING HOOK
 * Sends heartbeat every 30 seconds to maintain online status
 * Uses BeaconAPI for reliable cleanup on page unload
 */
export const usePresence = () => {
  const intervalRef = useRef(null);
  const isActiveRef = useRef(true);

  const sendHeartbeat = useCallback(async () => {
    if (!isActiveRef.current) return;

    try {
      await presenceApi.updatePresence();
    } catch (error) {
      // Silent fail for heartbeat
    }
  }, []);

  useEffect(() => {
    // Send initial heartbeat immediately
    sendHeartbeat();

    // Set up interval (every 30 seconds)
    intervalRef.current = setInterval(sendHeartbeat, 30000);

    // Handle visibility change (pause when tab not visible)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isActiveRef.current = false;
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      } else {
        isActiveRef.current = true;
        sendHeartbeat(); // Send immediately when tab becomes visible
        intervalRef.current = setInterval(sendHeartbeat, 30000);
      }
    };

    // Send heartbeat before page unload using fetch with keepalive
    const handleBeforeUnload = () => {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user?.token) {
        // Use fetch with keepalive instead of BeaconAPI
        // This allows us to send Authorization header while still being reliable
        try {
          fetch(`${API_BASE_URL}/feed/presence/heartbeat`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`, // ✅ TOKEN SENT HERE
            },
            body: JSON.stringify({}),
            keepalive: true, // ✅ Ensures request completes even if page closes
          });
        } catch (error) {
          // Silent fail - page is closing anyway
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      isActiveRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [sendHeartbeat]);

  return { sendHeartbeat };
};

export default usePresence;
